// @ts-check

import { ChatMessagePartSchema } from "@/components/chat-bubble/utils";
import z from "zod";
import { AgentMode } from "./utils";

const AgentModeSchema = z.enum(Object.values(AgentMode))
const ActiveStreamRecordSchema = z.union([
  z.object({
    type: z.literal("chat"),
    query: z.object({
      message: z.string().optional(),
      files: z.array(z.object({}).passthrough()).optional(),
      images: z.array(z.object({}).passthrough()).optional(),
      mode: AgentModeSchema.optional(),
    }),
    response: z.nullable(
      z.union([
        z.object({
          type: z.literal("content"),
          content: z.string(),
        }),
        z.object({
          type: z.literal("structured-content"),
          content: z.array(ChatMessagePartSchema),
        }),
      ])
    ),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("report"),
    query: z.object({
      modality: z.string(),
      patientInfo: z.object({}).passthrough(),
      image: z.object({}).passthrough(),
    }),
    response: z.nullable(
      z.union([
        z.object({
          type: z.literal("content"),
          content: z.string(),
        }),
        z.object({
          type: z.literal("structured-content"),
          content: z.lazy(() => (
            z.array(
              z.union([
                z.object({
                  event: AdvancedReportChunkDataContentSchema.options[0].shape.event,
                  data: AdvancedReportChunkDataContentSchema.options[0],
                }),
                z.object({
                  event: AdvancedReportChunkDataContentSchema.options[1].shape.event,
                  data: AdvancedReportChunkDataContentSchema.options[1],
                })
              ])
            )
          )),
        }),
      ])
    ),
    timestamp: z.string(),
  }),
])

const ChatStreamMetaSchema = z.object({
  message: z.string(),
  files: z.array(z.object({}).passthrough()).optional(),
  images: z.array(z.object({}).passthrough()).optional(),
  mode: AgentModeSchema.optional(),
})

const EditChatStreamMetaSchema = z.object({
  message: z.string(),
  mode: AgentModeSchema.optional(),
  editedMessageId: z.string(),
})

const RegenerateChatStreamMetaSchema = z.object({
  mode: AgentModeSchema.optional(),
  regeneratedMessageId: z.string(),
})

const ReportStreamMetaSchema = z.object({
  modality: z.string(),
  patientInfo: z.object({}).passthrough(),
  image: z.object({}).passthrough(),
})

const StreamMetaSchema = z.union([
  ChatStreamMetaSchema,
  EditChatStreamMetaSchema,
  RegenerateChatStreamMetaSchema,
  ReportStreamMetaSchema,
])

const ChatStreamContentChunkSchema = z.object({
  type: z.literal("chat"),
  data: z.object({
    content: z.string(),
  }),
})

const ReportStreamContentChunkSchema = z.object({
  type: z.literal("report"),
  data: z.object({
    content: z.string(),
  }),
})

const StreamContentChunkSchema = z.union([
  ChatStreamContentChunkSchema,
  ReportStreamContentChunkSchema,
])

const ChatStreamStructuredContentChunkSchema = z.object({
  type: z.literal("chat"),
  data: z.object({
    content: z.object({}).passthrough(),
  }),
})

const AdvancedReportChunkDataContentSchema = z.union([
  z.object({
    event: z.literal("intermediate"),
    content: z.string(),
  }),
  z.object({
    event: z.literal("report"),
    content: z.string(),
  }),
  z.object({
    event: z.literal("error"),
    error: z.string(),
    message: z.string(),
  }),
])
const ReportStreamStructuredContentChunkSchema = z.object({
  type: z.literal("report"),
  data: z.object({
    content: AdvancedReportChunkDataContentSchema,
  }),
})

const StreamStructuredContentChunkSchema = z.union([
  ChatStreamStructuredContentChunkSchema,
  ReportStreamStructuredContentChunkSchema,
])

const SessionInfoEventSchema = z.object({
  event: z.literal('session_info'),
  sessionId: z.string(),
  timestamp: z.string()
});

const RunStartedEventSchema = z.object({
  event: z.literal('RunStarted'),
  timestamp: z.string()
});

const RunCompletedEventSchema = z.object({
  event: z.literal('RunCompleted'),
  timestamp: z.string()
});

const RunResponseContentEventSchema = z.object({
  event: z.literal('RunResponseContent'),
  agent_id: z.string().optional(),
  content: z.string(),
  timestamp: z.string()
});

const RunContentEventSchema = z.object({
  event: z.literal('RunContent'),
  agent_id: z.string().optional(),
  content: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const ToolCallStartedEventSchema = z.union([
  z.object({
    event: z.literal('ToolCallStarted'),
    agent_id: z.string(),
    tool_name: z.string(),
    tool_call_id: z.string(),
    args: z.string(),
    timestamp: z.string(),
    sessionId: z.string().optional()
  }),
  z.object({
    event: z.literal('ToolCallStarted'),
    tool_name: z.string(),
    args: z.string(),
    timestamp: z.string()
  }),
  z.object({
    event: z.literal('ToolCallStarted'),
    agent_id: z.string().optional(),
    agent_name: z.string().optional(),
    timestamp: z.string(),
    sessionId: z.string().optional()
  }),
])

const ToolCallCompletedEventSchema = z.union([
  z.object({
    event: z.literal('ToolCallCompleted'),
    agent_id: z.string(),
    tool_name: z.string(),
    tool_call_id: z.string(),
    tool_result: z.string(),
    timestamp: z.string(),
  }),
  z.object({
    event: z.literal('ToolCallCompleted'),
    tool_name: z.string(),
    tool_result: z.string(),
    timestamp: z.string()
  }),
  z.object({
    event: z.literal('ToolCallCompleted'),
    agent_id: z.string().optional(),
    agent_name: z.string().optional(),
    content: z.string().optional(),
    timestamp: z.string(),
    sessionId: z.string().optional()
  }),
])

const ReasoningContentEventSchema = z.object({
  event: z.literal('ReasoningContent'),
  agent_id: z.string().optional(),
  content: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

// Canvas review progress; shown live, not saved with the message.
const ReviewProgressEventSchema = z.object({
  event: z.literal('ReviewProgress'),
  agent_id: z.string().optional(),
  progress: z.object({
    stage: z.string(),
    section: z.string().optional(),
    added: z.number().optional(),
    skipped: z.number().optional(),
    failed: z.boolean().optional(),
    elapsed_s: z.number().optional(),
  }).passthrough(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const EditDraftedEventSchema = z.object({
  event: z.literal('EditDrafted'),
  agent_id: z.string().optional(),
  progress: z.object({
    number: z.number(),
    edit: z.object({
      id: z.string().nullish(),
      old_string: z.string().nullish(),
      new_string: z.string().nullish(),
      rationale: z.string().nullish(),
    }).passthrough(),
    elapsed_s: z.number().optional(),
  }).passthrough(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const ReasoningStartedEventSchema = z.object({
  event: z.literal('ReasoningStarted'),
  agent_id: z.string().optional(),
  agent_name: z.string().optional(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const ReasoningStepContentSchema = z.object({
  title: z.string().optional(),
  action: z.string().nullable().optional(),
  result: z.any().nullable().optional(),
  reasoning: z.string().optional(),
  next_action: z.string().optional(),
  confidence: z.number().optional(),
});

const ReasoningStepEventSchema = z.object({
  event: z.literal('ReasoningStep'),
  agent_id: z.string().optional(),
  agent_name: z.string().optional(),
  content: ReasoningStepContentSchema.optional(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const ReasoningCompletedEventSchema = z.object({
  event: z.literal('ReasoningCompleted'),
  agent_id: z.string().optional(),
  agent_name: z.string().optional(),
  content: z.object({
    reasoning_steps: z.array(ReasoningStepContentSchema)
  }).passthrough().optional(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const PlanCreatedEventSchema = z.object({
  event: z.literal('PlanCreated'),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const PlanUpdatedEventSchema = z.object({
  event: z.literal('PlanUpdated'),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const DelegationStartedEventSchema = z.object({
  event: z.literal('DelegationStarted'),
  agent_id: z.string(),
  task: z.string(),
  message: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const DelegationCompletedEventSchema = z.object({
  event: z.literal('DelegationCompleted'),
  agent_id: z.string(),
  result: z.string(),
  message: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const SubAgentStartedEventSchema = z.object({
  event: z.literal('SubAgentStarted'),
  agent_id: z.string(),
  agent_name: z.string(),
  task: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
})

const SubAgentCompletedEventSchema = z.object({
  event: z.literal('SubAgentCompleted'),
  agent_id: z.string(),
  agent_name: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
})

const SubAgentRunStartedEventSchema = z.object({
  event: z.literal('SubAgentRunStarted'),
  agent_id: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
})

const SubAgentRunCompletedEventSchema = z.object({
  event: z.literal('SubAgentRunCompleted'),
  agent_id: z.string(),
  timestamp: z.string(),
  sessionId: z.string().optional()
})

const MemoryCleanedUpEventSchema = z.object({
  event: z.literal('MemoryCleanedUp'),
  timestamp: z.string(),
  sessionId: z.string().optional()
});

const ResearchCompleteEventSchema = z.object({
  event: z.literal('research_complete'),
  sessionId: z.string(),
  chat: z.object({}).passthrough(),
  timestamp: z.string()
});

const StreamCompleteEventSchema = z.object({
  event: z.literal('stream_complete'),
  sessionId: z.string(),
  chat: z.object({}).passthrough(),
  reports: z.array(
    z.object({
      reportId: z.string(),
      report: z.object({}).passthrough()
    })
  ).optional(),
  timestamp: z.string()
});

const ErrorEventSchema = z.object({
  event: z.literal('error'),
  error: z.string(),
  details: z.string().optional(),
  timestamp: z.string()
});

// Union schema for all possible agent events
const AgentChatChunkDataContentSchema = z.union([
  SessionInfoEventSchema,
  RunStartedEventSchema,
  RunCompletedEventSchema,
  RunResponseContentEventSchema,
  RunContentEventSchema,
  ToolCallStartedEventSchema,
  ToolCallCompletedEventSchema,
  ReasoningContentEventSchema,
  ReasoningStartedEventSchema,
  ReasoningStepEventSchema,
  ReasoningCompletedEventSchema,
  ReviewProgressEventSchema,
  EditDraftedEventSchema,
  PlanCreatedEventSchema,
  PlanUpdatedEventSchema,
  DelegationStartedEventSchema,
  DelegationCompletedEventSchema,
  SubAgentStartedEventSchema,
  SubAgentCompletedEventSchema,
  SubAgentRunStartedEventSchema,
  SubAgentRunCompletedEventSchema,
  MemoryCleanedUpEventSchema,
  ResearchCompleteEventSchema,
  StreamCompleteEventSchema,
  ErrorEventSchema,
]);

export {
  AgentModeSchema,
  ActiveStreamRecordSchema,

  StreamMetaSchema,
  ChatStreamMetaSchema,
  ReportStreamMetaSchema,
  EditChatStreamMetaSchema,
  RegenerateChatStreamMetaSchema,

  StreamContentChunkSchema,
  ChatStreamContentChunkSchema,
  ReportStreamContentChunkSchema,

  StreamStructuredContentChunkSchema,
  ChatStreamStructuredContentChunkSchema,
  ReportStreamStructuredContentChunkSchema,

  AgentChatChunkDataContentSchema,
  SessionInfoEventSchema,
  RunStartedEventSchema,
  RunCompletedEventSchema,
  RunResponseContentEventSchema,
  RunContentEventSchema,
  ToolCallStartedEventSchema,
  ToolCallCompletedEventSchema,
  ReasoningContentEventSchema,
  ReasoningStartedEventSchema,
  ReasoningStepEventSchema,
  ReasoningCompletedEventSchema,
  ReviewProgressEventSchema,
  EditDraftedEventSchema,
  PlanCreatedEventSchema,
  PlanUpdatedEventSchema,
  DelegationStartedEventSchema,
  DelegationCompletedEventSchema,
  MemoryCleanedUpEventSchema,
  ResearchCompleteEventSchema,
  StreamCompleteEventSchema,
  ErrorEventSchema,
}
